import api from './api';

// Talks to /api/people/* — the People module (headcount & labour cost).
// SENSITIVE: pay figures are encrypted at rest on the server; the API returns
// decrypted amounts only to authenticated callers. Version (and site, for
// structures) come from the x-budget-version-id / x-site-code headers set by
// the api interceptor.
export const peopleService = {
  // What is costed, by component and site, plus the job titles with no GL
  // mapping that will therefore post nowhere. `oncosts` is the SINGLE source of
  // the UIF/SDL percentages — the page must use these rather than its own
  // constants, or the figure on screen and the figure in the ledger drift.
  getPeopleCosts: async () => {
    const res = await api.get('/people/costs');
    return res.data;
  },

  // Rebuilds calc_people_costs for the whole version. A full rebuild, not an
  // incremental one: a deleted position has no row to update, and an
  // incremental calc would leave its salary in the ledger forever.
  //
  // Skips any position with a function_code — those are planned on shifts and
  // already costed through calc_labour_costs.
  calculatePeopleCosts: async () => {
    const res = await api.post('/people/costs/calculate');
    return res.data;
  },

  // ---- Grades (salary bands, per version) ----
  getGrades: async () => {
    const res = await api.get('/people/grades');
    return res.data;
  },
  saveGrades: async (grades) => {
    const res = await api.put('/people/grades', { grades });
    return res.data;
  },
  deleteGrade: async (gradeCode) => {
    const res = await api.delete('/people/grades', { data: { grade_code: gradeCode } });
    return res.data;
  },

  // ---- Structures (nominal roll, per version + site) ----
  // Returns { positions, grades }.
  // Paging is OPT-IN: omit `limit` and the server returns everything, exactly
  // as before. With it, `data` carries total / hasMore alongside the positions.
  getStructures: async ({ filters, limit, offset } = {}) => {
    const res = await api.get('/people/structures', {
      params: {
        ...(filters && Object.keys(filters).length ? { filters: JSON.stringify(filters) } : {}),
        ...(limit ? { limit } : {}),
        ...(offset ? { offset } : {}),
      },
    });
    return res.data;
  },

  // Distinct values for one Structures column, fetched when its menu opens.
  getStructureFacets: async (col) => {
    const res = await api.get('/people/structures/facets', { params: { col } });
    return res.data;
  },
  saveStructures: async (positions) => {
    const res = await api.put('/people/structures', { positions });
    return res.data;
  },
  deleteStructure: async (positionId) => {
    const res = await api.delete('/people/structures', { data: { position_id: positionId } });
    return res.data;
  },

  // ---- Job Titles (catalogue, per version) ----
  // Returns { jobTitles, grades }.
  getJobTitles: async () => {
    const res = await api.get('/people/job-titles');
    return res.data;
  },
  saveJobTitles: async (jobTitles) => {
    const res = await api.put('/people/job-titles', { jobTitles });
    return res.data;
  },
  deleteJobTitle: async (jobTitleId) => {
    const res = await api.delete('/people/job-titles', { data: { job_title_id: jobTitleId } });
    return res.data;
  },

  // ---- GL mapping (Site + Job Title → GL account + cost centre, per version) ----
  // Returns { mappings, accounts, costCenters }.
  getGlMap: async () => {
    const res = await api.get('/people/gl-map');
    return res.data;
  },
  saveGlMap: async (mappings) => {
    const res = await api.put('/people/gl-map', { mappings });
    return res.data;
  },
  deleteGlMap: async (mapId) => {
    const res = await api.delete('/people/gl-map', { data: { map_id: mapId } });
    return res.data;
  },
};

export default peopleService;
